import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PeriodContext } from 'App/components/CurrentPeriod'
import Loading from 'App/components/Loading/Loading'
import EmptyClasses from 'App/views/TeacherClasses/components/EmptyClasses'
import { filterUniqueArrayByName } from 'App/views/TeacherClasses/components/utils'
import { GET_CLASSES_TOTAL_COUNT_VOUCHER, GET_LEVELS_VOUCHER } from 'graphql/queries'
import PropTypes from 'prop-types'
import { LevelsContext, PersonContext } from 'src/App/views/TeacherClasses/TeacherClassesContexts'
import UserContext from 'stores/UserStore'

import { canChangeSchoolClassesAndLevels, graphqlClient } from 'utils'
import { ROUTES } from 'utils/constants'

const ClassesTotal = ({ children }) => {
  const periodIds = [...useContext(PeriodContext)]
  const userStore = useContext(UserContext)
  const currentSchool = userStore.getCurrentSchool()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const getClassTotal = async () => {
    try {
      const { data } = await graphqlClient().query({
        query: GET_CLASSES_TOTAL_COUNT_VOUCHER,
        variables: {
          accessCode: currentSchool?.accessCode || userStore.schoolAccessCode,
          periodIds: periodIds.map(period => +period.id),
        },
      })

      setData(data)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getClassTotal()
  }, [])

  const lastPeriod = () => ({ ...periodIds.pop() })
  const formatPersonData = ({ person, school }) => ({
    id: person.id,
    name: person.name,
    school: { ...school },
  })

  if (
    !canChangeSchoolClassesAndLevels({
      byIntegration: currentSchool?.byIntegration,
      schoolBrands: currentSchool?.brands,
      userExternalReference: userStore?.externalReference,
    })
  ) {
    window.PLURALL_ELO_TOAST_CONTAINER.addToast({
      variant: 'negative',
      text: 'Edição de dados escolares indisponível devido a integração automática, procure a escola para a alteração dos dados.',
      seconds: 10,
    })
    navigate(ROUTES.PERFIL_DADOS_ESCOLARES)
  }

  if (error) {
    window.PLURALL_ELO_TOAST_CONTAINER.addToast({
      variant: 'negative',
      text: 'Não foi possível carregar os dados da escola. Por favor, aguarde um instante e tente novamente.',
    })

    navigate(ROUTES.PERFIL_DADOS_ESCOLARES)
  }

  if (loading) {
    return <Loading fullscreen />
  }

  if (data.school?.classes?.totalCount === 0) {
    return <EmptyClasses period={lastPeriod()} schoolName={data.school.tradeName} />
  }

  return <PersonContext.Provider value={formatPersonData(data)}>{children}</PersonContext.Provider>
}

const SchoolLevels = ({ children }) => {
  const [schoolData, setSchoolData] = useState(null)
  const userStore = useContext(UserContext)
  const currentSchool = userStore.getCurrentSchool()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  const getClassLevelVoucher = async () => {
    try {
      const { data } = await graphqlClient().query({
        query: GET_LEVELS_VOUCHER,
        variables: {
          accessCode: currentSchool.accessCode || userStore.schoolAccessCode,
        },
      })

      setData(data)
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getClassLevelVoucher()
  }, [])

  const getUniqueLevels = levels => filterUniqueArrayByName(levels)

  const parseSchoolLevels = schoolLevels => {
    const uniqueLevelsData = getUniqueLevels(schoolLevels.levels)
    setSchoolData(uniqueLevelsData)
  }

  if (error) {
    navigate(ROUTES.PERFIL_DADOS_ESCOLARES)
  }

  if (loading) {
    return <Loading fullscreen />
  }

  if (data.school && !schoolData) {
    return parseSchoolLevels(data.school)
  }

  return <LevelsContext.Provider value={schoolData}>{children}</LevelsContext.Provider>
}

SchoolLevels.propTypes = {
  children: PropTypes.node.isRequired,
}

ClassesTotal.propTypes = {
  children: PropTypes.node.isRequired,
}

export { ClassesTotal, SchoolLevels }
